/**
 * AIQuickSetup — guided setup for free-tier AI providers (Gemini, Groq, Claude)
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  TextField,
  Link,
  Chip,
  Alert,
} from '@mui/material';
import {
  OpenInNew,
  CheckCircle,
  Bolt,
  Psychology,
  AutoAwesome,
} from '@mui/icons-material';
import { PROVIDERS, PROVIDER_INFO, setApiKey } from '../services/llmService';

const SETUP_STEPS = [
  {
    provider: PROVIDERS.GEMINI,
    icon: <AutoAwesome sx={{ fontSize: 18 }} />,
    color: '#4285f4',
    title: 'Google Gemini 2.5 Flash',
    subtitle: 'Most generous free tier — 1,500 requests/day, 1M token context',
    steps: [
      'Open Google AI Studio (no credit card required)',
      'Sign in with your Google account',
      'Click "Create API Key" and copy it',
      'Paste the key below and click Save',
    ],
    signupUrl: 'https://aistudio.google.com/app/apikey',
    badge: 'Recommended',
  },
  {
    provider: PROVIDERS.GROQ,
    icon: <Bolt sx={{ fontSize: 18 }} />,
    color: '#f97316',
    title: 'Groq (Qwen3.6)',
    subtitle: 'Fastest responses — sub-second inference, generous free tier',
    steps: [
      'Create a free Groq account (no credit card)',
      'Go to API Keys in the console',
      'Create a key and copy it',
      'Paste the key below and click Save',
    ],
    signupUrl: 'https://console.groq.com/keys',
    badge: 'Fastest',
  },
  {
    provider: PROVIDERS.CLAUDE,
    icon: <Psychology sx={{ fontSize: 18 }} />,
    color: '#d97757',
    title: 'Claude Sonnet 5',
    subtitle: 'Best reasoning for CBSE math & science — API key from Anthropic Console',
    steps: [
      'Create an account at console.anthropic.com',
      'Go to Settings → API Keys',
      'Create a key (free credits may be available for new accounts)',
      'Paste the key below and click Save',
    ],
    signupUrl: 'https://console.anthropic.com/settings/keys',
    badge: 'Best Quality',
  },
];

function AIQuickSetup({ onKeySaved, onSyncToFirestore }) {
  const [activeStep, setActiveStep] = useState(0);
  const [keyInputs, setKeyInputs] = useState({});
  const [savedProviders, setSavedProviders] = useState({});
  const [saving, setSaving] = useState(null);

  const handleSave = async (provider) => {
    const key = keyInputs[provider]?.trim();
    if (!key) return;

    setSaving(provider);
    try {
      await setApiKey(provider, key);
      setSavedProviders(prev => ({ ...prev, [provider]: true }));
      if (onSyncToFirestore) {
        await onSyncToFirestore(provider, key);
      }
      if (onKeySaved) onKeySaved(provider);
    } finally {
      setSaving(null);
    }
  };

  const completedCount = Object.keys(savedProviders).length;

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', mb: 2 }}>
      <Box sx={{ px: 2, py: 1.5, bgcolor: 'primary.50', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" fontWeight={700}>
          Quick AI Setup
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Get Ekamanam running in under 2 minutes — all providers below offer free tiers with no credit card.
        </Typography>
      </Box>

      {completedCount > 0 && (
        <Alert severity="success" sx={{ mx: 2, mt: 1.5, py: 0.5, fontSize: '0.8rem' }}>
          {completedCount} provider{completedCount !== 1 ? 's' : ''} configured — AI features are ready!
        </Alert>
      )}

      <Box sx={{ p: 2 }}>
        <Stepper activeStep={activeStep} orientation="vertical" nonLinear>
          {SETUP_STEPS.map((step, index) => {
            const info = PROVIDER_INFO[step.provider];
            const isSaved = savedProviders[step.provider];

            return (
              <Step key={step.provider} completed={isSaved}>
                <StepLabel
                  optional={
                    <Chip label={step.badge} size="small" sx={{ height: 18, fontSize: '0.65rem', ml: 0.5 }} />
                  }
                  onClick={() => setActiveStep(index)}
                  sx={{ cursor: 'pointer' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ color: step.color }}>{step.icon}</Box>
                    <Typography variant="body2" fontWeight={600}>{step.title}</Typography>
                    {isSaved && <CheckCircle color="success" sx={{ fontSize: 16 }} />}
                  </Box>
                </StepLabel>
                <StepContent>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {step.subtitle}
                  </Typography>

                  <Box component="ol" sx={{ m: 0, pl: 2.5, mb: 1.5 }}>
                    {step.steps.map((s, i) => (
                      <Typography key={i} component="li" variant="caption" sx={{ mb: 0.25 }}>
                        {s}
                      </Typography>
                    ))}
                  </Box>

                  <Link
                    href={step.signupUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="caption"
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, mb: 1.5 }}
                  >
                    Get free API key <OpenInNew sx={{ fontSize: 12 }} />
                  </Link>

                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder={info?.signupUrl ? 'Paste your API key here…' : 'API key…'}
                      value={keyInputs[step.provider] || ''}
                      onChange={(e) => setKeyInputs(prev => ({ ...prev, [step.provider]: e.target.value }))}
                      disabled={isSaved}
                      type="password"
                      sx={{ '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      disabled={!keyInputs[step.provider]?.trim() || saving === step.provider || isSaved}
                      onClick={() => handleSave(step.provider)}
                      sx={{ textTransform: 'none', whiteSpace: 'nowrap', minWidth: 64 }}
                    >
                      {isSaved ? 'Saved' : saving === step.provider ? '…' : 'Save'}
                    </Button>
                  </Box>

                  <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
                    {index > 0 && (
                      <Button size="small" onClick={() => setActiveStep(index - 1)} sx={{ textTransform: 'none' }}>
                        Back
                      </Button>
                    )}
                    {index < SETUP_STEPS.length - 1 && (
                      <Button size="small" onClick={() => setActiveStep(index + 1)} sx={{ textTransform: 'none' }}>
                        {isSaved ? 'Next provider' : 'Skip for now'}
                      </Button>
                    )}
                  </Box>
                </StepContent>
              </Step>
            );
          })}
        </Stepper>
      </Box>
    </Paper>
  );
}

export default AIQuickSetup;
